import React, { useState } from 'react';
import { Button, Chip, Card, CardBody } from '@heroui/react';
import { MagnifyingGlass, X, User, Buildings, FileText, Hammer, Headset, Wrench, ShoppingCart } from '@phosphor-icons/react';
import SearchComponent from 'shared/components/searchBar/components/searchComponent';
import { ResourceType } from 'shared/types/resource';

interface EntitySearchBarProps {
  entityType: string; // e.g., 'contact', 'company', 'building'
  entityInstance: string; // e.g., 'contact1', 'contact2', 'company'
  displayName: string; // e.g., 'Contact #1', 'Company'
  selectedEntity: any;
  onEntitySelect: (entity: any) => void;
  onEntityClear: () => void;
  isLoading?: boolean;
}

const EntitySearchBar: React.FC<EntitySearchBarProps> = ({
  entityType,
  entityInstance,
  displayName,
  selectedEntity,
  onEntitySelect,
  onEntityClear,
  isLoading = false
}) => {
  const [showSearch, setShowSearch] = useState(false);

  // Map entity types to resource types and icons
  const getEntityConfig = (entityType: string) => {
    const configs: Record<string, {
      resourceTypes: ResourceType[];
      icon: React.ReactNode;
      searchLabel: string;
      endpoint?: string;
    }> = {
      contact: {
        resourceTypes: [ResourceType.CONTACT],
        icon: <User size={16} />,
        searchLabel: 'Zoek contact...',
      },
      company: {
        resourceTypes: [ResourceType.COMPANIES],
        icon: <Buildings size={16} />,
        searchLabel: 'Zoek vennootschap (naam of KBO)...',
        endpoint: '/companies/search-for-documents',
      },
      contact_company: {
        resourceTypes: [ResourceType.COMPANY],
        icon: <Buildings size={16} />,
        searchLabel: 'Zoek leverancier/contact bedrijf...',
      },
      building: {
        resourceTypes: [ResourceType.BUILDING],
        icon: <Buildings size={16} />,
        searchLabel: 'Zoek gebouw...',
      },
      unit: {
        resourceTypes: [ResourceType.UNIT],
        icon: <Buildings size={16} />,
        searchLabel: 'Zoek unit...',
      },
      contract: {
        resourceTypes: [ResourceType.CONTRACT],
        icon: <FileText size={16} />,
        searchLabel: 'Zoek contract...',
      },
      tenant: {
        resourceTypes: [ResourceType.CONTACT], // Tenants are contacts
        icon: <User size={16} />,
        searchLabel: 'Zoek huurder...',
      },
      acquisition: {
        resourceTypes: [ResourceType.BUILDING],
        icon: <ShoppingCart size={16} />,
        searchLabel: 'Zoek acquisitie (ID of naam)...',
        endpoint: '/acquisitions/search/for-documents',
      },
      renovation_project: {
        resourceTypes: [ResourceType.RENOVATION],
        icon: <Hammer size={16} />,
        searchLabel: 'Zoek renovatieproject (ID of titel)...',
        endpoint: '/renovation/projects/search-for-documents',
      },
      ticket: {
        resourceTypes: [ResourceType.HELPDESK],
        icon: <Headset size={16} />,
        searchLabel: 'Zoek helpdesk ticket (ID of titel)...',
        endpoint: '/helpdesk/tickets/search-for-documents',
      },
      project_part: {
        resourceTypes: [ResourceType.RENOVATION],
        icon: <Wrench size={16} />,
        searchLabel: 'Zoek project onderdeel (ID of titel)...',
        endpoint: '/renovation/parts/search-for-documents',
      }
    };

    return configs[entityType] || {
      resourceTypes: [ResourceType.CONTACT],
      icon: <MagnifyingGlass size={16} />,
      searchLabel: `Zoek ${entityType}...`,
    };
  };

  const config = getEntityConfig(entityType);

  const handleEntitySelect = (resource: any) => {
    console.log('Selected entity for', entityInstance, ':', resource);
    onEntitySelect(resource);
    setShowSearch(false);
  };

  const handleClearEntity = () => {
    onEntityClear();
    setShowSearch(false);
  };

  return (
    <div className="space-y-2">
        <label className="block text-sm font-medium text-neutral-primaryDark">
          {displayName}
        </label>
      
      {selectedEntity ? (
        // Show selected entity
        <Card>
          <CardBody className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {config.icon}
                <div>
                  <p className="font-medium text-sm">{selectedEntity.name}</p>
                  {selectedEntity.metadata1 && (
                    <p className="text-xs text-neutral-secondaryMid">
                      {selectedEntity.metadata1}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Chip size="sm" variant="flat" color="success">
                  Geselecteerd
                </Chip>
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  isIconOnly
                  onPress={handleClearEntity}
                >
                  <X size={14} />
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        // Show search interface
        <div className="space-y-2">
          {!showSearch ? (
            <Button
              variant="bordered"
              className="w-full justify-start"
              startContent={config.icon}
              onPress={() => setShowSearch(true)}
              isLoading={isLoading}
            >
              {config.searchLabel}
            </Button>
          ) : (
            <Card>
              <CardBody className="p-3 space-y-3 min-h-[300px]">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    Selecteer {displayName.toLowerCase()}
                  </p>
                  <Button
                    size="sm"
                    variant="light"
                    isIconOnly
                    onPress={() => setShowSearch(false)}
                  >
                    <X size={14} />
                  </Button>
                </div>
                
                <SearchComponent
                  resourceFilter={config.resourceTypes}
                  onSelect={handleEntitySelect}
                  label={config.searchLabel}
                  customEndpoint={config.endpoint}
                />
              </CardBody>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default EntitySearchBar;
